import { useEffect, useState } from 'react';
import { Card, CheckBox, Label, Select } from '@iwoca/orion';
import { Trans, t } from '@lingui/macro';
import { useAmplitude } from 'hooks/useAmplitude';
import { TDurationDetails } from 'types';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { DurationFilter } from '../DurationFilter/DurationFilter';
import { MIN_REPAYMENT_AMOUNT } from '../filterConfigs.helper';
import { OverpaymentAmountFilter } from '../OverpaymentAmountFilter/OverpaymentAmountFilter';
import styles from './OverpaymentOptions.module.css';

type TProps = {
  showOverpayment: boolean;
  maxTermLength: number;
  overpaymentAmount?: string;
  onOverpaymentDurationChange: (duration: TDurationDetails) => void;
  onOverpaymentChange: (amount?: string) => void;
  setShowOverpayment: (shown: boolean) => void;
};

type TOverpaymentOption = 'timeframe' | 'repaymentAmount';

export const OverpaymentOptions = ({
  showOverpayment,
  maxTermLength,
  overpaymentAmount,
  onOverpaymentDurationChange,
  onOverpaymentChange,
  setShowOverpayment,
}: TProps) => {
  const { trackEvent } = useAmplitude();
  const [selectedOverpaymentOption, setSelectedOverpaymentOption] =
    useState<TOverpaymentOption>('timeframe');

  useEffect(() => {
    if (showOverpayment) {
      // If timeframe is selected, set overpayment amount to undefined (as timeframe and repaymentAmount can not be used together)
      if (selectedOverpaymentOption === 'timeframe') {
        onOverpaymentChange(undefined);
        return;
      }

      // If repaymentAmount is selected, set endDate to null and overpayment amount to default min value
      const data = {
        startDate: null,
        endDate: null,
        duration: maxTermLength.toString(),
      };

      onOverpaymentDurationChange(data);
      onOverpaymentChange(MIN_REPAYMENT_AMOUNT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOverpayment, selectedOverpaymentOption]);

  const onCalculateOverpaymentClick = () => {
    // Reset custom values back to default if user unselects the calculate overpayment option
    if (showOverpayment) {
      const data = {
        startDate: null,
        endDate: null,
        duration: maxTermLength.toString(),
      };

      onOverpaymentChange(undefined);
      onOverpaymentDurationChange(data);
    }

    trackEvent(AMPLITUDE_EVENTS.calculateOverpaymentClicked);
    setShowOverpayment(!showOverpayment);
  };

  return (
    <>
      <CheckBox
        label={t`Calculate overpayment?`}
        onChange={onCalculateOverpaymentClick}
        data-testid="overpayment-checkbox"
        checked={showOverpayment}
      />
      {showOverpayment && (
        <Card className={styles.card}>
          <div className={styles.selectContainer}>
            <Label className={styles.label} htmlFor="overpayment-option">
              <Trans>Calculate by</Trans>
            </Label>
            <Select
              id="overpayment-option"
              value={selectedOverpaymentOption}
              onChange={(e) =>
                setSelectedOverpaymentOption(
                  e.target.value as TOverpaymentOption,
                )
              }
            >
              <option value="timeframe">
                <Trans>Timeframe</Trans>
              </option>
              <option value="repaymentAmount">
                <Trans>Repayment amount</Trans>
              </option>
            </Select>
          </div>
          {selectedOverpaymentOption === 'timeframe' && (
            <DurationFilter
              maxTermLength={maxTermLength}
              onChange={onOverpaymentDurationChange}
            />
          )}
          {selectedOverpaymentOption === 'repaymentAmount' && (
            <OverpaymentAmountFilter
              value={overpaymentAmount}
              onChange={onOverpaymentChange}
            />
          )}
        </Card>
      )}
    </>
  );
};
